import { v4 as uuidv4 } from "uuid";
import { formatPostalCode } from "@/Helpers/formatPostalCode";
import { lineItemsRecommendFreightClassFromDimensions } from "@/Shared/LineItems/Helpers/lineItemsRecommendFreightClassFromDimensions";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { HandlingUnitType } from "@freightsimple/generated-dashboard-openapi-client/models/HandlingUnitType";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { TemperatureHandling } from "@freightsimple/generated-dashboard-openapi-client/models/TemperatureHandling";
import type {
  BookShipmentSlice2Type,
  ShipmentDraftState,
} from "@/Shared/Types/bookShipmentSliceTypes";
import { bookShipmentFindSavedLocationIndex } from "@/Features/BookShipment/Helpers/bookShipmentFindSavedLocationIndex";
import { initialBookShipmentSliceState } from "@/Features/BookShipment/Helpers/bookShipmentEmptyState";
import { bookShipmentDropHiddenDeadline } from "@/Features/BookShipment/Helpers/bookShipmentDropHiddenDeadline";
import { removeUnavailableAccessorialsForLocation } from "@/Shared/Locations/Helpers/recommendedAccessorialsForLocation";

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function bookShipmentLoadDraftState(payload: {
  draft: ShipmentDraftState;
  savedLocations: Array<SavedLocation>;
}): BookShipmentSlice2Type {
  const draft = payload.draft;
  const savedLocations = payload.savedLocations;
  const state = initialBookShipmentSliceState();

  state.draft = draft;

  // Set shipment draft ID
  state.shipment.branchId = draft.branchId;

  // Map pickup location from draft
  if (draft.pickup) {
    const {
      locationType,
      distributionWarehouseBrand,
      businessName,
      notes,
      openFrom,
      openUntil,
      contactPhoneNumber,
      contactEmail,
      contactName,
      contactPhoneNumberExtension,
      countryCode,
      boothNumber,
      city,
      deadline,
      stateOrProvinceCode,
      addressLine,
      addressLine2,
      postalCode,
      referenceNumber,
      specialServiceInsideRequired,
      specialServiceLiftGateRequired,
      hasShippingDock,
      hasForklift,
    } = draft.pickup;

    const accessorials = [];

    if (specialServiceInsideRequired) accessorials.push("LOGISTICS_INSIDE");

    if (specialServiceLiftGateRequired)
      accessorials.push("LOGISTICS_LIFT_GATE_REQUIRED");

    state.shipment.pickupLocation = {
      locationType,
      distributionWarehouseBrand,
      businessName,
      latitudeLongitude: payload.draft.pickupLatitudeLongitude,
      address: {
        addressLine,
        addressLine2,
        city,
        stateOrProvinceCode,
        postalCode: postalCode ? formatPostalCode(postalCode) : undefined,
        countryCode,
      },
      // A draft can predate the rule, or come from an API caller, so it can
      // still pair residential with inside.
      accessorials: removeUnavailableAccessorialsForLocation(
        locationType,
        accessorials,
      ),
      notes: notes || "",
      hours: {
        openFrom: openFrom || "10:00",
        openUntil: openUntil || "17:00",
      },
    };

    // Set pickup contact if available
    if (contactName || contactPhoneNumber || contactEmail) {
      state.shipment.pickupContact = {
        contactName: contactName || "",
        phoneNumber: contactPhoneNumber || "",
        phoneNumberExtension: contactPhoneNumberExtension || "",
        emailAddress: contactEmail || "",
      };
    }

    state.shipment.pickupReferenceNumber = referenceNumber || "";
    state.shipment.pickupBoothNumber =
      locationType === LocationType.TradeShow ? boothNumber : undefined;

    state.shipment.pickupDeadline = deadline?.toISOString().split("T")[0];

    // Check if pickup location matches a saved location
    const pickupIndex = bookShipmentFindSavedLocationIndex(
      state.shipment.pickupLocation,
      savedLocations,
    );

    const selectedPickupLocation =
      pickupIndex === -1 ? undefined : savedLocations[pickupIndex];
    if (selectedPickupLocation !== undefined) {
      state.enteringNewPickup = false;
      state.selectedPickupSavedLocationId =
        selectedPickupLocation.savedLocationId;
      state.shipment.pickupLocation.locationType =
        selectedPickupLocation.location.locationType;
      state.shipment.pickupLocationHasDock =
        selectedPickupLocation.locationHasDock;
      state.shipment.pickupLocationHasForklift =
        selectedPickupLocation.locationHasForklift;
    } else {
      state.enteringNewPickup = true;
      state.selectedPickupSavedLocationId = undefined;
      state.shipment.pickupLocationHasDock = hasShippingDock;
      state.shipment.pickupLocationHasForklift = hasForklift;
    }
  }

  // Map delivery location from draft
  if (draft.delivery) {
    const {
      locationType,
      distributionWarehouseBrand,
      businessName,
      notes,
      openFrom,
      openUntil,
      contactPhoneNumber,
      contactEmail,
      contactName,
      contactPhoneNumberExtension,
      countryCode,
      boothNumber,
      city,
      deadline,
      stateOrProvinceCode,
      addressLine,
      addressLine2,
      postalCode,
      referenceNumber,
      amazonReferenceId,
      amazonShipmentId,
      specialServiceInsideRequired,
      specialServiceLiftGateRequired,
      specialServiceAppointmentRequired,
      hasShippingDock,
      hasForklift,
    } = draft.delivery;

    const accessorials = [];

    if (specialServiceInsideRequired) accessorials.push("LOGISTICS_INSIDE");

    if (specialServiceLiftGateRequired)
      accessorials.push("LOGISTICS_LIFT_GATE_REQUIRED");

    if (specialServiceAppointmentRequired)
      accessorials.push("SCHEDULING_APPOINTMENT_REQUIRED");

    state.shipment.deliveryLocation = {
      locationType,
      distributionWarehouseBrand,
      businessName,
      latitudeLongitude: payload.draft.deliveryLatitudeLongitude,
      address: {
        addressLine,
        addressLine2,
        city,
        stateOrProvinceCode,
        postalCode: postalCode ? formatPostalCode(postalCode) : undefined,
        countryCode,
      },
      // A draft can predate the rule, or come from an API caller, so it can
      // still pair residential with inside.
      accessorials: removeUnavailableAccessorialsForLocation(
        locationType,
        accessorials,
      ),
      notes: notes || "",
      hours: {
        openFrom: openFrom || "10:00",
        openUntil: openUntil || "17:00",
      },
    };

    // Set delivery contact if available
    if (contactName || contactPhoneNumber || contactEmail) {
      state.shipment.deliveryContact = {
        contactName: contactName || "",
        phoneNumber: contactPhoneNumber || "",
        phoneNumberExtension: contactPhoneNumberExtension || "",
        emailAddress: contactEmail || "",
      };
    }

    state.shipment.deliveryReferenceNumber = referenceNumber || "";
    state.shipment.deliveryAmazonReferenceId = amazonReferenceId;
    state.shipment.deliveryAmazonShipmentId = amazonShipmentId;
    state.shipment.deliveryBoothNumber =
      locationType === LocationType.TradeShow ? boothNumber : undefined;
    state.shipment.deliveryDeadline = deadline?.toISOString().split("T")[0];

    // Check if delivery location matches a saved location
    const deliveryIndex = bookShipmentFindSavedLocationIndex(
      state.shipment.deliveryLocation,
      savedLocations,
    );
    const selectedDeliveryLocation =
      deliveryIndex === -1 ? undefined : savedLocations[deliveryIndex];
    if (selectedDeliveryLocation !== undefined) {
      state.enteringNewDelivery = false;
      state.selectedDeliverySavedLocationId =
        selectedDeliveryLocation.savedLocationId;
      state.shipment.deliveryLocation.locationType =
        selectedDeliveryLocation.location.locationType;
      state.shipment.deliveryLocationHasDock =
        selectedDeliveryLocation.locationHasDock;
      state.shipment.deliveryLocationHasForklift =
        selectedDeliveryLocation.locationHasForklift;
    } else {
      state.enteringNewDelivery = true;
      state.selectedDeliverySavedLocationId = undefined;
      state.shipment.deliveryLocationHasDock = hasShippingDock;
      state.shipment.deliveryLocationHasForklift = hasForklift;
    }
  }

  // Map line items from draft
  if (draft.lineItems) {
    state.shipment.lineItems = draft.lineItems.map((li) => {
      const recommendedFreightClass =
        li.weightPerHandlingUnitPounds &&
        li.lengthInches &&
        li.widthInches &&
        li.heightInches
          ? lineItemsRecommendFreightClassFromDimensions(
              li.weightPerHandlingUnitPounds,
              li.lengthInches,
              li.widthInches,
              li.heightInches,
            ).freightClass
          : undefined;

      return {
        lineItemId: uuidv4(),
        handlingUnitType: li.handlingUnitType ?? HandlingUnitType.Pallet,
        numberHandlingUnits: li.numberHandlingUnits ?? 1,
        description: li.description || "",
        length: li.lengthInches,
        width: li.widthInches,
        height: li.heightInches,
        weightPerHandlingUnit: li.weightPerHandlingUnitPounds,
        temperatureHandling:
          li.temperatureHandling ?? TemperatureHandling.NoSpecialHandling,
        isDangerous: li.isDangerous || false,
        nmfcItemNumber: li.nmfcItemNumber,
        isStackable: li.isStackable,
        freightClass: recommendedFreightClass,
        dangerousUnNumber: li.dangerousUnNumber,
        dangerousClassification: li.dangerousClassification,
        dangerousPackingGroup: li.dangerousPackingGroup,
        dangerousProperShippingName: li.dangerousProperShippingName,
        dangerousTechnicalName: li.dangerousTechnicalName,
        dangerousNumberPackages: li.dangerousNumberPackages,
        dangerousPackagingType: li.dangerousPackagingType,
        dangerousNetExplosiveQuantityKg: li.dangerousNetExplosiveQuantityKg,
      };
    });
  }

  // Map broker from draft
  if (draft.broker) {
    state.shipment.broker = {
      ...draft.broker,
      address: { ...draft.broker.address },
      contact: { ...draft.broker.contact },
    };
  }

  // Map insurance from draft
  if (draft.insurance) {
    state.shipment.addInsuranceToShipment = !!draft.insurance;
    state.shipment.insuranceAmount = draft.insurance.amount || 0;
    state.shipment.insuranceCurrency = draft.insurance.currency || Currency.Cad;
    state.shipment.stateOfGoods = draft.insurance.stateOfGoods;
  }

  if (draft.emergencyContact) {
    state.shipment.emergencyContactDetails = {
      contactName: draft.emergencyContact.contactName ?? "",
      contactPhoneNumber: draft.emergencyContact.contactPhoneNumber ?? "",
      contactPhoneNumberExtension:
        draft.emergencyContact.contactPhoneNumberExtension ?? "",
      ...(draft.emergencyContact.erapPlanNumber
        ? { erapPlanNumber: draft.emergencyContact.erapPlanNumber }
        : {}),
      ...(draft.emergencyContact.erapPhoneNumber
        ? { erapPhoneNumber: draft.emergencyContact.erapPhoneNumber }
        : {}),
      ...(draft.emergencyContact.tdgResponsibleName
        ? { tdgResponsibleName: draft.emergencyContact.tdgResponsibleName }
        : {}),
    };
  }

  // Last word on the deadlines, because matching the address book above can
  // still change a location's type
  bookShipmentDropHiddenDeadline(state.shipment, LocationContext.Pickup);
  bookShipmentDropHiddenDeadline(state.shipment, LocationContext.Delivery);

  return state;
}
